import { IChildConfiguration, IChildQuestion, IChildSKU, IParentChildRelation, ISKU } from "../types";
import { ChildQuestionInput } from "./child_question_input";
import { QuestionModal } from "./question_modal";

export type SelectionMap = Map<number, number>;

/**
 * Handles controls for a single child configuration.
 */
export class ChildConfigControls {
    id: number;
    is_offline_configuration: boolean;
    is_reconfig: boolean;
    parent_child_relation: IParentChildRelation;
    child_config: IChildConfiguration;
    config_controls: HTMLCollectionOf<HTMLInputElement>;
    current_selections: SelectionMap;
    child_sku_selections: Map<number, SelectionMap>;
    update_callback: Function;
    current_sku: ISKU;
    child_sku_id: number = 0;
    default_question: ChildQuestionInput;
    child_questions: Map<number, {data: IChildQuestion, template: HTMLElement}> = new Map();
    product_sku_image: HTMLImageElement;
    product_sku_thumbnail: HTMLImageElement;
    has_multiple_input_question: boolean;
    carousel_initialized: boolean;

    constructor(
        parent_child_relation_data: IParentChildRelation,
        update_callback: Function,
        update_quantity: Function,
        previous_templates: Map<number, {data: IChildQuestion, template: HTMLElement}> = null,
        is_offline_configuration = false,
        is_reconfig = false,
        question_modals: Map<number, QuestionModal>
    ) {
        this.parent_child_relation = parent_child_relation_data;
        this.is_offline_configuration = is_offline_configuration;
        this.is_reconfig = is_reconfig;
        this.child_config = parent_child_relation_data.childConfiguration;
        this.update_callback = update_callback;
        this.id = this.parent_child_relation.id;

        let product_images = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('cm-image-previewer');
        let product_thumbnails = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('cm-thumbnails-mini');

        if (this.is_offline_configuration) {
            this.product_sku_image = <HTMLImageElement>document.getElementById('js-product-main-image');
        } else if (product_images.length && product_thumbnails.length) {
            const preview_id = (product_thumbnails[0] as HTMLElement).dataset.caParent.split('_').pop();

            this.product_sku_image = <HTMLImageElement>document.getElementById(`det_img_${preview_id}_sku_${this.id}`);
            this.product_sku_thumbnail = <HTMLImageElement>document.getElementById(`det_img_${preview_id}_sku_${this.id}_mini`);
        }

        this.has_multiple_input_question = false;
        for (const child_question of this.child_config.childQuestions) {
            if (child_question.question.type.name === 'Multiple Input'
                || child_question.question.type.name === 'Compound Multiple Input') {
                this.has_multiple_input_question = true;
            }

            const child_question_element = document.getElementById(`js-child-question-${child_question.id}-${this.id}`);
            if (child_question_element) {
                const template = child_question_element.cloneNode(true) as HTMLElement;
                template.classList.remove('hidden');
                this.child_questions.set(child_question.id, {data: child_question, template});
                child_question_element.remove();
            } else if (previous_templates) {
                const template = previous_templates.get(child_question.id).template;
                this.child_questions.set(child_question.id, {data: child_question, template});
            }
        }

        const default_question = this.child_config.childQuestions.find(cq => cq.default);
        if (this.parent_child_relation.defaultSelections) {
            this.current_selections = new Map();
            for (let key in this.parent_child_relation.defaultSelections) {
                this.current_selections.set(
                    Number(key),
                    this.parent_child_relation.defaultSelections[key]
                );
            }

            this.default_question = new ChildQuestionInput(
                this.child_questions,
                default_question.id,
                this.parent_child_relation.id,
                this.selections_updated.bind(this),
                update_quantity,
                this.current_selections,
                question_modals
            );
        } else {
            this.default_question = new ChildQuestionInput(
                this.child_questions,
                default_question.id,
                this.parent_child_relation.id,
                this.selections_updated.bind(this),
                update_quantity,
                null,
                question_modals
            );
            this.current_selections = this.default_question.get_question_branch_selections();
        }

        this.child_sku_selections = new Map();
        // Child SKUs are null for Child Question Preview.
        if (!this.child_config.childSKUs) {
            return;
        }

        for (const child_sku of this.child_config.childSKUs) {
            const selections = new Map();
            for (const selection of child_sku.childSKUSelections) {
                selections.set(selection.questionId, selection.questionOptionId);
            }
            this.child_sku_selections.set(child_sku.id, selections);
        }

        this.find_sku_from_selections();
    }

    selections_updated(child_question_input: ChildQuestionInput, question_added: boolean = false, use_callback: boolean = true, option_changed: boolean = true) : void {
        if (!option_changed) {
            this.update_callback(null, false, option_changed);
            return;
        }
        
        if (!question_added) {
            this.current_selections = this.default_question.get_question_branch_selections();
        }
        // Check for propagation.
        const propagating_question = child_question_input?.child_question_data.propagate ? child_question_input : null;
        if (use_callback) {
            this.update_callback(propagating_question, question_added);
        }
    }

    update_sku_image(): void {
        if (!this.product_sku_image) {
            return;
        }
        if (this.is_offline_configuration) {
            if (this.current_sku && this.current_sku.imageUrl) {
                this.product_sku_image.src = this.current_sku.imageUrl;
            }
            else {
                this.product_sku_image.src = this.product_sku_image.dataset.defaultImageUrl;
            }
            return;
        }
        // Set sku product image to current SKU's image URL
        if (this.current_sku && this.current_sku.imageUrl) {
            this.product_sku_image.src = this.current_sku.imageUrl;
            // If product preview thumbnail exists, click it instead.
            const product_prevew_thumbnail = document.getElementById('det_img_product-preview-container_mini');
            if (product_prevew_thumbnail) {
                product_prevew_thumbnail.parentElement.click();
            }
            else if (this.current_sku.thumbnailImageUrl && this.current_sku.largeImageUrl) {
                (this.product_sku_image.parentElement as HTMLAnchorElement).href = this.current_sku.largeImageUrl;
                if (this.product_sku_thumbnail) {
                    this.product_sku_thumbnail.src = this.current_sku.thumbnailImageUrl;
                    this.product_sku_thumbnail.click();
                }
            }
  
            // OwlCarousel width is sometimes wrong on initial load, so its widths need to be recalculated.
            if (!this.carousel_initialized && $(".owl-carousel").data("owlCarousel")) {
                $(".owl-carousel").data("owlCarousel").calculateAll();
                this.carousel_initialized = true;
            }
        }
    }

    find_sku_from_selections() : void {
        for (const [child_sku_id, child_sku_selections] of Array.from(this.child_sku_selections.entries())) {
            let child_sku_is_match = true;
            if (!child_sku_selections.size) {
                continue;
            }

            for (const [question_id, sku_selected_option] of child_sku_selections) {
                // If the current selection option is different from the child SKU selection, then it isn't a match.
                const current_selected_option = this.current_selections.get(question_id);
                if (sku_selected_option !== current_selected_option) {
                    child_sku_is_match = false;
                    break;
                };
            };

            if (child_sku_is_match) {
                const child_sku = this.child_config.childSKUs.find(child_sku => (
                    child_sku.id === child_sku_id
                ));
                this.current_sku = child_sku.sku || null;
                this.child_sku_id = child_sku_id;
                break;
            } else {
                this.current_sku = null;
            }
        }

        this.update_current_sku();

    }

    update_current_sku(selected_child_sku: IChildSKU = null) {
        if (selected_child_sku) { 
            this.current_sku = selected_child_sku.sku;
            this.child_sku_id = selected_child_sku.id;
        }

        // Update the SKUs image
        this.update_sku_image();

        // Check to see if the SKU is free shipping or not, update accordingly.
        const free_shipping_indicator = document.getElementById('js-free-shipping-indicator');
        const free_shipping_label = document.getElementById('js-free-shipping-label');
        if(free_shipping_indicator) {
            if (this.current_sku.freeShipping) {
                free_shipping_indicator.classList.remove('hidden');
                if (free_shipping_label) {
                    free_shipping_label.classList.remove('tv-display__none');
                }
            } else {
                free_shipping_indicator.classList.add('hidden');
                if (free_shipping_label) {
                    free_shipping_label.classList.add('tv-display__none');
                }
            }
        }

    }

}
